import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import { ChapterInfo } from "../types/ChapterInfo";

const menuItem = {
  letterSpacing: "normal",
  lineHeight: "normal",
  paddingLeft: "5px",
  marginLeft: "0px",
  paddingRight: "5px",
  marginRight: "0px",
  width: "310px",
  // subtle hover/selected to improve readability without heavy color
  "&:hover": { backgroundColor: "#f5f5f5" },
  "&.Mui-selected": {
    backgroundColor: "#eef5ff",
    "&:hover": { backgroundColor: "#e8f0ff" },
  },
} as const;

const menuItemTitle = {
  margin: "10px 0 0 0",
  fontWeight: 600,           // softer than bold
  color: "#444",             // not pure black
  pointerEvents: "none" as const,
  borderBottom: "1px solid #eee",
} as const;

const chapterNameTrans = {
  position: "absolute" as const,
  left: "200px",
  color: "#555",             // lighter to differentiate second column
  fontSize: "0.92rem",       // slightly smaller
} as const;

const verticalSpacer = { height: "5px" } as const;

// Hoisted util
function verseNumberToString(verseNumber: number): string {
  let s = "";
  if (verseNumber < 100) s += "\xa0 ";
  if (verseNumber < 10) s += "\xa0 ";
  return s + verseNumber;
}

interface MenuChaptersProps {
  anchor: HTMLElement | null;                 // allow null when closed
  chaptersInfo: ChapterInfo[];
  chapterClicked: (chapterIndex: number) => void; // expects 0-based index
  handleMenuClose: () => void;
  currentChapterNumber: number;
}

function MenuChaptersImpl(props: MenuChaptersProps): JSX.Element {
  const { anchor, chaptersInfo, chapterClicked, handleMenuClose, currentChapterNumber } = props;
  const open = Boolean(anchor);

  // Single handler for all items (avoids N closures)
  const onItemClick = React.useCallback(
    (e: React.MouseEvent<HTMLLIElement>) => {
      const idxStr = (e.currentTarget as HTMLLIElement).dataset.index;
      if (idxStr == null) return;
      const idx = Number(idxStr);
      if (!Number.isNaN(idx)) {
        chapterClicked(idx);
      }
    },
    [chapterClicked],
  );

  return (
    <Menu
      anchorEl={anchor}
      open={open}
      onClose={handleMenuClose}
      keepMounted
      transitionDuration={0} // skip open/close animation for faster paint
      aria-label="Chapters menu"
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <MenuItem sx={[menuItem, menuItemTitle]}>
        {/* pad to align with numbers below */}
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Chapters
        <Box component="span" sx={chapterNameTrans}>
          Surahs
        </Box>
      </MenuItem>

      <Box sx={verticalSpacer} />

      {chaptersInfo.map((item, index) => (
        <MenuItem
          key={`chapter-menu-${index}`}
          data-index={index}
          selected={currentChapterNumber === index + 1}
          onClick={onItemClick}
          sx={menuItem}
          disableRipple
        >
          &nbsp;{verseNumberToString(index + 1)}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {item.nameEnglish}
          <Box component="span" sx={chapterNameTrans}>
            {item.nameTrans}{" "}
          </Box>
        </MenuItem>
      ))}
      <Box m={1} />
    </Menu>
  );
}

// Re-render only when inputs that affect rendering change
export default React.memo(
  MenuChaptersImpl,
  (a, b) =>
    a.anchor === b.anchor &&
    a.currentChapterNumber === b.currentChapterNumber &&
    a.chaptersInfo === b.chaptersInfo && // parent should pass stable ref; usually static
    a.chapterClicked === b.chapterClicked &&
    a.handleMenuClose === b.handleMenuClose,
);
